<template>
  <template v-if="isProfileView">
    <div class="grid-x border padding-bottom-l profile-reviews-list-item" :class="`margin-bottom-${isLastReview ? 'm' : 'l'}`">
      <div class="cell large-1 padding-right-xs show-for-large">
        <img v-if="review.productData?.imgUrl" :src="review.productData?.imgUrl" :alt="`Image of ${review.productData?.name}`" />
        <div v-else class="placeholder-image">
          <span class="picture-dark-medium"></span>
        </div>
      </div>
      <div class="cell large-auto">
        <div class="grid-x large-margin-bottom-z small-margin-bottom-xs">
          <div class="cell small-3 medium-2 large-1 padding-right-xs hide-for-large">
            <img v-if="review.productData?.imgUrl" :src="review.productData?.imgUrl" :alt="`Image of ${review.productData?.name}`" />
            <div v-else class="placeholder-image">
              <span class="picture-dark-medium"></span>
            </div>
          </div>
          <div class="cell small-9 medium-10 large-auto">
            <div class="grid-x">
              <div class="cell grid-x large-5 small">
                <div class="review-stars-sm">
                  <ReviewStarsRating :key="review.rating" :rating="review.rating" :show-rating="false" />
                </div>
                <span class="margin-right-xxs">{{ timeSinceReviewSubmission }}</span>
              </div>
              <div v-if="review.productData?.url" class="cell hide-for-large margin-top-xxs margin-bottom-xxs">
                <template v-if="review.productData?.available">
                  <a :href="review.productData?.url" target="_blank" class="link-body">{{ review.productData?.productBrandAndName }}</a>
                </template>
                <template v-else>
                  <span>{{ review.productData?.productBrandAndName }}</span>
                </template>
              </div>
              <div class="cell grid-x large-auto align-right badges-section">
                <div v-if="hasBadges" class="badges-section grid-x shrink align-right medium-12 large-auto">
                  <span v-if="review.incentivizedReview" class="margin-bottom-xxs" :class="{ 'margin-right-xs': review.badges?.Staff }">
                    <BadgeBasic label="Incentivized review" />
                  </span>
                  <span v-if="review.badges?.Staff" class="margin-bottom-xxs">
                    <BadgeBasic :label="staffBadgeText" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="grid-x">
          <div v-if="review.productData?.url" class="cell show-for-large margin-bottom-xs" :class="{ 'margin-top-xxs': !hasBadges }">
            <template v-if="review.productData?.available">
              <a :href="review.productData?.url" target="_blank" class="link-body">{{ review.productData?.productBrandAndName }}</a>
            </template>
            <template v-else>
              <span>{{ review.productData?.productBrandAndName }}</span>
            </template>
          </div>
          <div v-once class="cell large margin-bottom-xxs" v-html="sanitize(review.title)"></div>
          <div v-once class="cell" v-html="sanitize(review.reviewText)"></div>
          <div v-if="computedMedia.length" class="cell">
            <ul class="cell grid-x grid-margin-x align-middle images-container">
              <li v-for="media in computedMedia" :key="media.id" class="cell small-4 large-3 margin-top-xs">
                <!-- convert to reusable template later -->
                <ReviewThumbnail
                  :is-video="media.type === 'video'"
                  :src="media?.thumbnailUrl ?? media?.sizes?.normal?.url"
                  :alt="`${media.type === 'video' ? 'Video' : 'Photo'} from ${computedUserNickname}`"
                />
              </li>
            </ul>
          </div>
          <div v-if="hasAdditionalFields" class="cell margin-top-xs">
            <div>
              <div v-for="field in additionalFields" :key="field.id" class="grid-x align-bottom additional-field">
                <span class="heavy margin-right-xxs">{{ field.label }}:</span>
                <span class="small">{{ field.value }}</span>
              </div>
              <div v-for="field in contextDataFields" :key="field.id" class="grid-x align-bottom additional-field">
                <span class="heavy-sm margin-right-xxs">{{ field.dimensionLabel }}</span>
                <span class="small">{{ field.valueLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <div v-else :id="reviewDivId" ref="reviewCommentContainer">
    <span v-if="hasValidExternalSource" class="source-product grid-x align-middle margin-bottom-xs" :class="{ 'margin-top-m': !isFullReview }">
      <img :src="sourceProduct?.imageUrl" :alt="sourceProduct.name" class="source-icon margin-right-xxs small-1" />
      <span class="external-review-source">
        This review is from
        <a :href="sourceProduct.productPageUrl" class="link-sm">{{ sourceProduct.name }}</a>
      </span>
    </span>
    <div
      class="review-item padding-bottom-m"
      :class="{
        'border grid-x grid-margin-x': !isFullReview,
        'margin-top-m': !hasValidExternalSource && !isFullReview,
      }"
    >
      <div class="cell grid-y small-12 large-3 review-item-section margin-bottom-xs">
        <div class="grid-x shrink align-middle small" :class="{ 'margin-bottom-xxs': hasBadges || review.userLocation }">
          <div class="review-stars-sm">
            <ReviewStarsRating :key="review.rating" :rating="review.rating" :show-rating="false" />
          </div>
          <Username
            v-if="computedUserNickname"
            :user-nickname="computedUserNickname"
            :author-id="review.authorId"
            :is-syndicated="review.syndicated"
            :is-employee-author="isEmployeeAuthor"
            class="margin-right-xxs"
          />
          <span class="margin-right-xxs">{{ timeSinceReviewSubmission }}</span>
        </div>
        <div v-if="review.userLocation" class="small" :class="{ 'margin-bottom-xxs': hasBadges }">from {{ review.userLocation }}</div>
        <div v-if="hasBadges" class="grid-x shrink align-middle">
          <span v-if="review.incentivizedReview" class="margin-right-xs margin-bottom-xxs">
            <Badge label="Incentivized review" :tooltip-body="BADGES_TOOLTIPS.incentivizedReview" />
          </span>
          <span v-if="review.badges?.Staff" class="margin-bottom-xxs">
            <Badge :label="staffBadgeText" :tooltip-body="staffBadgeTooltipBody" />
          </span>
        </div>
        <div v-if="hasAdditionalFields" :class="{ 'margin-top-xxs': !hasBadges }">
          <div v-for="field in additionalFields" :key="field.id" class="grid-x align-bottom additional-field">
            <span class="heavy margin-right-xxs">{{ field.label }}:</span>
            <span class="small">{{ field.value }}</span>
          </div>
          <div v-for="field in contextDataFields" :key="field.id" class="grid-x align-bottom additional-field">
            <span class="heavy-sm margin-right-xxs">{{ field.dimensionLabel }}</span>
            <span class="small">{{ field.valueLabel }}</span>
          </div>
        </div>
        <div
          v-if="!isFullReview && secondaryRatings.length > 0"
          class="slider grid-y show-for-large"
          :class="hasAdditionalFields || !hasBadges ? 'margin-top-s' : 'margin-top-xs'"
        >
          <FitSlider
            v-for="(rating, index) in secondaryRatings"
            :key="rating.id"
            :labels="rating.labels"
            :title="rating.label"
            :value="rating.value"
            :value-range="rating.valueRange"
            :class="{ 'margin-top-s': index > 0 }"
          />
        </div>
      </div>
      <div class="right-side cell small-12 large-auto">
        <div class="review-item-section margin-bottom-xs">
          <h4 v-if="review.title" class="h3 review-title margin-bottom-xxs large" v-html="sanitize(review.title)"></h4>
          <ExpandedReviewComment :key="review.reviewId" :content="reviewText" />
        </div>
        <div
          v-if="secondaryRatings.length > 0"
          class="grid-y small-12 margin-bottom-xs"
          :class="{ 'hide-for-large': !isFullReview, slider: !isFullReview }"
        >
          <FitSlider
            v-for="(rating, index) in secondaryRatings"
            :key="rating.id"
            :labels="rating.labels"
            :title="rating.label"
            :value="rating.value"
            :value-range="rating.valueRange"
            :class="{ 'margin-top-xs': index > 0 }"
          />
        </div>
        <p v-if="review.recommended" class="heavy margin-bottom-xs grid-x align-middle">
          Recommends this product<span class="checkmark-spacing" :class="'confirmation-without-circle-dark-medium'"></span>
        </p>
        <ul v-if="computedMedia.length && !isFullReview" class="grid-x grid-margin-x align-middle images-container">
          <li v-for="(media, index) in computedMedia" :key="media.id" class="cell small-4 large-3 margin-bottom-xs">
            <button class="media-button" @click="displayFullReview(index, $event)">
              <!-- convert to reusable template later -->
              <ReviewThumbnail
                :is-video="media.type === 'video'"
                :src="media?.thumbnailUrl ?? media?.sizes?.normal?.url"
                :alt="`${media.type === 'video' ? 'Video' : 'Photo'} from ${computedUserNickname}`"
              />
            </button>
          </li>
        </ul>
        <div v-if="review.syndicated && review.syndicationSource">
          <div class="grid-x align-middle">
            <p class="small syndicated-text">Customer review from {{ review.syndicationSource.name }}</p>
          </div>
        </div>
        <ClientResponse
          v-for="response in computedClientResponses"
          :key="response.id"
          :department="response.department"
          :submission-date="response.date"
          :source-client-name="response.sourceClientName"
          :response="response.response"
          class="margin-top-xxs"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, useId } from 'vue';
import { sanitize } from '@vue-util/sanitize';
import ReviewStarsRating from '@atomic-ui/reviewlet/Reviewlet';
import Username from '~/modules/reviews/components/UsernameView';
import ClientResponse from '~/modules/reviews/components/ClientResponse';
import Badge from '~/modules/reviews/components/BadgeView';
import BadgeBasic from '~/modules/reviews/components/BadgeBasic';
import ExpandedReviewComment from '~/modules/reviews/components/ExpandedReviewComment';
import ReviewThumbnail from '~/modules/reviews/components/Media/ReviewThumbnail.vue';
import { capitalizeOnlyFirstWord } from '~/utils/capitalize';
import { useBrand } from '~/composables/useBrand';
import { usePageData } from '~/composables/usePageData';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useReviews from '~/modules/reviews/composables/useReviews';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { brandName } from 'config';
import { trackInViewAndViewedUCG } from '~/utils/bvPixel';
import FitSlider from './FitSlider';
import {
  BADGES_TOOLTIPS,
  DEFAULT_MCOM_NATIVE_USER_NICKNAME,
  DEFAULT_SYNDICATED_USER_NICKNAME,
  DEFAULT_BCOM_NATIVE_USER_NICKNAME,
  LOADING_STATE,
} from '../constants/reviews';
import { getTimeElapsed } from '../../../utils/getTimeElapsed';

/** @typedef {import('~~/shared/globalTypes/reviews').Review} ReviewType */

const props = defineProps({
  review: {
    /** @type import('vue').PropType<ReviewType> */
    type: Object,
    required: true,
  },
  isFullReview: {
    type: Boolean,
    default: false,
  },
  isProfileView: {
    type: Boolean,
    default: false,
  },
  isLastReview: {
    type: Boolean,
    default: false,
    required: false,
  },
});
// Generates a base unique identifier for this component instance to ensure stable DOM IDs
const uuid = useId();
const { isShopTheSetFlag } = useProductFlags();

const {
  killswitches: { reviewVideoUploadEnabled = false },
} = useKillswitches();

const getUUIDName = (index, suffix) => `${uuid}-${index}--${suffix ?? 'generic'}`;
const reviewCommentContainer = ref(null);
const reviewDivId = props.isFullReview ? `review-full-modal-${props.review.reviewId}` : `review-list-item-${props.review.reviewId}`;

onMounted(() => {
  // BV track for stats - these run for each review item loaded on the page when a review is scrolled into view
  if (!props.isProfileView) {
    trackInViewAndViewedUCG(props.review, reviewDivId);
  }
});
const { searchQuery, relatedProducts, loadingState } = useReviews();
const hasBadges = computed(() => !!(props.review?.badges?.Staff || props.review?.incentivizedReview));
const { productId } = usePageData();
const { isBcom } = useBrand();
// TODO: this logic will be refactored as part of PDPM-1951
// Logic to determine the displayed nickname, handling anonymous, syndicated, and brand-specific fallbacks
const computedUserNickname = computed(() => {
  // NOTE: anonymous works well with 2.0 but not with 1.0
  // TODO: When switching to 2.0 is done remove userNickname and displayName
  if ((!props.review?.userNickname && !props.review?.displayName) || props?.review?.anonymous) {
    if (props.review?.syndicated) {
      return DEFAULT_SYNDICATED_USER_NICKNAME;
    }
    if (isBcom) {
      return DEFAULT_BCOM_NATIVE_USER_NICKNAME;
    }
    return DEFAULT_MCOM_NATIVE_USER_NICKNAME;
  }
  return props.review?.userNickname ?? props.review.displayName;
});

/**
 * @param {string} text
 */
const wrapWithHeavy = (text) => `<span class="heavy">${text}</span>`;
// Highlights search terms within the review text using regex while maintaining sanitization
const reviewText = computed(() =>
  searchQuery.value && loadingState.value === LOADING_STATE.LOADED
    ? sanitize(props.review.reviewText).replace(new RegExp(searchQuery.value, 'gi'), wrapWithHeavy)
    : sanitize(props.review.reviewText),
);
// badges
const staffBadgeText = `${brandName} employee`;

const isEmployeeAuthor = computed(() => !!props.review?.badges?.Staff);

// format staff toggletip text
const staffBadgeTooltipBody = BADGES_TOOLTIPS.staff(brandName);

/**
 *
 * @param {*} rating
 */
const getLabels = (rating) => ({
  minLabel: rating?.minLabel,
  midLabel: rating?.midLabel,
  maxLabel: rating?.maxLabel,
});
// FIT information
const rawRatings = computed(() => props.review?.secondaryRatings || {});
const secondaryRatings = computed(() =>
  Object.keys(rawRatings.value).map((key) => {
    const rating = rawRatings.value[key];
    return {
      ...rating,
      labels: getLabels(rating),
      id: getUUIDName(key, 'sr'),
    };
  }),
);

// additional fields
/** @type {import('vue').ComputedRef<{ [k:String]: any}>} */
const contextDataFields = computed(() =>
  (props.review?.contextDataValuesOrder ?? []).map((key) => ({
    ...props.review.contextDataValues[key],
    id: getUUIDName(key, 'cdv'),
  })),
);
/** @type {import('vue').ComputedRef<{ [k:String]: any}>} */
const additionalFields = computed(() =>
  Object.keys(props.review?.additionalFields || {})
    .filter((key) => key !== 'OrderNumber')
    .map((key) => ({
      ...props.review.additionalFields[key],
      label: capitalizeOnlyFirstWord(props.review.additionalFields[key].label),
      id: getUUIDName(key, 'af'),
    })),
);
const hasAdditionalFields = computed(() => contextDataFields.value.length > 0 || additionalFields.value.length > 0);

const computedClientResponses = computed(
  () =>
    props.review.clientResponses?.map((response, index) => {
      return {
        ...response,
        id: getUUIDName(index, 'ccr'),
      };
    }) || [],
);

const computedMedia = computed(() => {
  const photos =
    props.review?.photos?.map((photo, index) => ({
      ...photo,
      id: getUUIDName(index, 'photos'),
      type: 'photo',
    })) || [];
  const videos =
    (reviewVideoUploadEnabled &&
      props.review?.videos?.map((video, index) => ({
        ...video,
        id: getUUIDName(index, 'videos'),
        type: 'video',
      }))) ||
    [];
  return [...photos, ...videos];
});

const timeSinceReviewSubmission = computed(() => getTimeElapsed(props.review.submissionTime));

// Review source banner|link
const isExternalReview = computed(() => String(props.review.productId) !== productId);
const sourceProduct = computed(() => (isExternalReview.value && !isShopTheSetFlag ? relatedProducts.value[props.review.productId] : null));

const productHasHyperLinkFields = () => ['productPageUrl', 'name'].every((key) => key in sourceProduct.value);
const hasValidExternalSource = computed(() => isExternalReview.value && sourceProduct.value && productHasHyperLinkFields());

const emit = defineEmits(['onSelectPhoto']);

/**
 * Opens the full review modal with the selected media item.
 * @param {number} selectedPhotoIndex - Flat index from the combined media list
 */
function displayFullReview(selectedPhotoIndex, event) {
  const img = event?.target;
  emit('onSelectPhoto', props.review, selectedPhotoIndex, img?.naturalWidth ? img.naturalWidth / img.naturalHeight : null);
}
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~@csgIcons/new/picture-dark-medium';
@import '~@csgIcons/new/confirmation-without-circle-dark-medium';

.syndicated-logo {
  height: 20px;
  max-width: 100px;
}

.media-button {
  width: 100%;
  height: 100%;
}

.placeholder-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f1f1f1;
}

.syndicated-text {
  color: $gray-1-color;
}

.checkmark-spacing {
  margin-left: rem-calc(4);
}

.slider {
  @include breakpoint(large) {
    max-width: rem-calc(300);
  }
}

.images-container li {
  max-height: rem-calc(240);
  overflow: hidden;
}

.source-product span {
  color: $gray-1-color;
}

.source-icon {
  height: rem-calc(20);
  max-width: rem-calc(20);
}

.right-side {
  overflow: hidden;
}

.border {
  border-bottom: $gray-3-border;
}

@include breakpoint(medium down) {
  .profile-summary-row,
  .badges-section {
    justify-content: unset;
  }
}

.max-w-708 {
  max-width: rem-calc(708);
}

.external-review-source {
  flex: 1;
}

// this was changed from h3 tag p
// override is added to comply with previous spacing, that is overriden on Product.vue level
// when decided to remmove override for h3 spacing  on Product.vue - this can be deleted too
// There is TODO for that TD#1
h4.review-title {
  @include breakpoint(large up) {
    margin-bottom: rem-calc(24) !important;
  }
}
</style>
